import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { JobCard } from "@/components/JobCard";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Search, DollarSign, Mail, BarChart3, ArrowRight, CheckCircle2, Globe } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1622612023350-b15f063eabe6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ jobs: 0, states: 0 });

  useEffect(() => {
    api.get("/jobs").then(({ data }) => {
      setJobs(data.slice(0, 6));
      setStats({ jobs: data.length, states: new Set(data.map((j) => (j.remote ? "Remote" : j.state))).size });
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Free browse banner */}
      <div className="bg-navy-800 text-white">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-2.5 text-center text-sm">
          <span className="font-semibold text-amber-500">Free to browse all safety jobs.</span>{" "}
          Applicants pay $10 for 30 days unlimited applications • Employers pay $99 per 60-day listing
        </div>
      </div>

      {/* Hero */}
      <section className="relative grid-bg overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wide mb-6">
              <ShieldCheck className="h-3.5 w-3.5" /> Safety Jobs · U.S. Safety Careers
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-navy-800 text-balance">
              The home of <span className="text-amber-500">safety careers</span> in America
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-lg leading-relaxed">
              Safety Jobs connects EHS pros and the companies that need them. Browse every U.S. safety role free — apply to as many as you want for $10, or hire in minutes for $99.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" data-testid="hero-browse-btn" onClick={() => navigate("/jobs")} className="bg-navy-800 hover:bg-navy-900 text-white gap-2 h-12 px-6">
                <Search className="h-4 w-4" /> Browse jobs by state
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-post-btn" onClick={() => navigate("/post-job")} className="border-navy-800 text-navy-800 hover:bg-navy-50 gap-2 h-12 px-6">
                Post a job — $99 <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-10 flex gap-8">
              <div><div className="font-display text-3xl font-extrabold text-navy-800">{stats.jobs}</div><div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Live Jobs</div></div>
              <div><div className="font-display text-3xl font-extrabold text-navy-800">{stats.states}</div><div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">States Hiring</div></div>
              <div><div className="font-display text-3xl font-extrabold text-navy-800">60<span className="text-amber-500">d</span></div><div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Listing Term</div></div>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -inset-4 bg-amber-500/10 rounded-3xl -rotate-2" />
            <img src={HERO} alt="Safety worker" className="relative rounded-2xl shadow-2xl w-full h-[440px] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-xl p-4 flex items-center gap-3 border border-slate-100">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-emerald-600" /></div>
              <div><div className="text-sm font-bold text-navy-800">Instant publish</div><div className="text-xs text-slate-500">Live the moment you pay</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-amber-600">For Employers</div>
            <div className="flex items-baseline gap-2 mt-3"><span className="font-display text-5xl font-extrabold text-navy-800">$99</span><span className="text-slate-500">/ 60-day listing</span></div>
            <ul className="mt-6 space-y-3 text-sm text-slate-600">
              {["Publishes instantly after payment", "Applicant tracking dashboard", "Applications emailed to your hiring inbox", "Renewal reminders 5 days before expiry"].map((f) => (
                <li key={f} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
            <Button data-testid="pricing-post-btn" onClick={() => navigate("/post-job")} className="w-full mt-8 bg-navy-800 hover:bg-navy-900 text-white">Post a job</Button>
          </div>
          <div className="bg-navy-800 rounded-2xl p-8 shadow-lg text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 h-40 w-40 bg-amber-500/20 rounded-full blur-3xl" />
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-amber-500">For Applicants</div>
            <div className="flex items-baseline gap-2 mt-3"><span className="font-display text-5xl font-extrabold">$10</span><span className="text-slate-300">/ 30 days</span></div>
            <ul className="mt-6 space-y-3 text-sm text-slate-200">
              {["Apply to unlimited jobs for 30 days", "Upload resume & cover letter once", "One-click apply to any listing", "Browsing is always free"].map((f) => (
                <li key={f} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
            <Button data-testid="pricing-membership-btn" onClick={() => navigate("/membership")} className="w-full mt-8 bg-amber-500 hover:bg-amber-600 text-navy-900 font-bold">Get membership</Button>
          </div>
        </div>
      </section>

      {/* Latest jobs */}
      {jobs.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy-800">Latest safety jobs</h2>
            <Link to="/jobs" className="text-sm font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((j) => <JobCard key={j.job_id} job={j} />)}
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy-800 text-center mb-12">Everything runs on one clean workflow</h2>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: DollarSign, t: "Employer pays $99", d: "Post a listing and pay via Stripe. It publishes immediately." },
            { icon: Search, t: "Applicants browse free", d: "Jobs are grouped by U.S. state in alphabetical order." },
            { icon: Globe, t: "$10 unlocks applying", d: "Members apply to unlimited jobs for 30 days." },
            { icon: Mail, t: "Employers get notified", d: "Applications are emailed and tracked in dashboards." },
          ].map((s) => (
            <div key={s.t} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <div className="h-11 w-11 rounded-lg bg-amber-100 flex items-center justify-center mb-4"><s.icon className="h-5 w-5 text-amber-600" /></div>
              <div className="font-display font-bold text-navy-800">{s.t}</div>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
